import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Clock, MapPin, Camera, LogIn, LogOut as LogOutIcon } from "lucide-react";
import { formatTimeWAT, formatDateDDMMYYYY } from "@/lib/format";

const dutyHeading = (activeClockin, nextShift) => {
  if (activeClockin) return "Active shift";
  if (nextShift) return "Ready to clock in";
  return "No upcoming shift";
};

export const GuardHome = () => {
  const { user } = useAuth();
  const [activeClockin, setActiveClockin] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [sites, setSites] = useState([]);
  const [insideFence, setInsideFence] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const r1 = await api.get("/clockins?active_only=true");
    const mine = r1.data.find(c => c.guard_id === user.guard_id);
    setActiveClockin(mine || null);
    const r2 = await api.get("/shifts");
    setShifts(r2.data);
  }, [user?.guard_id]);

  useEffect(() => {
    refresh();
    api.get("/sites").then(r => setSites(r.data));
  }, [refresh]);

  // Pick next upcoming shift
  const nextShift = shifts
    .filter(s => new Date(s.end_at) > new Date() && s.status !== "MISSED")
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))[0];

  const site = nextShift ? sites.find(s => s.id === nextShift.site_id) : null;

  const clockIn = async () => {
    if (!site) return toast.error("No assigned site");
    setBusy(true);
    try {
      // Simulate GPS reading with site coordinates + jitter
      const lat = site.lat + (Math.random() - 0.5) * 0.001;
      const lng = site.lng + (Math.random() - 0.5) * 0.001;
      const r = await api.post("/clockins/clock-in", {
        shift_id: nextShift?.id, site_id: site.id, lat, lng,
        inside_geofence: insideFence, override_reason: insideFence ? null : "Visual confirmation",
        selfie: null,
      });
      setActiveClockin(r.data);
      toast.success(insideFence ? "Clocked in — inside fence" : "Clocked in — supervisor notified", { description: site.name });
    } catch (e) { toast.error("Clock in failed"); }
    finally { setBusy(false); }
  };

  const clockOut = async () => {
    if (!activeClockin) return;
    setBusy(true);
    try {
      await api.post("/clockins/clock-out", { clockin_id: activeClockin.id, lat: activeClockin.lat, lng: activeClockin.lng });
      setActiveClockin(null);
      toast.success("Clocked out · submit your DAR before leaving");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="s-card" style={{ background: activeClockin ? "rgba(45, 158, 95, 0.08)" : "var(--shield-navy-2)",
                                       borderColor: activeClockin ? "rgba(45, 158, 95, 0.3)" : "var(--border-dark)" }}>
        <div className="text-muted" style={{ fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {activeClockin ? "On duty" : "Off duty"}
        </div>
        <div className="h-page" style={{ marginTop: 6 }}>
          {dutyHeading(activeClockin, nextShift)}
        </div>
        {site && (
          <div className="text-muted flex items-center gap-2" style={{ fontSize: 13, marginTop: 8 }}>
            <MapPin size={14} /> {site.name}
          </div>
        )}
        {nextShift && (
          <div className="text-muted mono" style={{ fontSize: 12, marginTop: 4 }}>
            {formatDateDDMMYYYY(nextShift.start_at)} · {formatTimeWAT(nextShift.start_at)} → {formatTimeWAT(nextShift.end_at)}
          </div>
        )}
      </div>

      {!activeClockin && nextShift && (
        <>
          <label className="flex items-center gap-3 s-card" style={{ padding: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={insideFence} onChange={(e) => setInsideFence(e.target.checked)} data-testid="inside-fence" />
            <div>
              <div style={{ fontSize: 14, color: "#fff" }}>I am inside the post boundary</div>
              <div className="text-muted" style={{ fontSize: 12 }}>Uncheck only if GPS is off — supervisor will be alerted.</div>
            </div>
          </label>
          <button onClick={clockIn} disabled={busy} className="btn-primary w-full tap-target"
                  style={{ padding: "20px", fontSize: 18 }} data-testid="clock-in-btn">
            <LogIn size={20} /> Clock in
          </button>
          <div className="text-muted flex items-center gap-2" style={{ fontSize: 12, justifyContent: "center" }}>
            <Camera size={12} /> Selfie verification simulated for demo
          </div>
        </>
      )}

      {activeClockin && (
        <>
          <div className="s-card flex items-center gap-3">
            <Clock size={18} color="#2ECAC8" />
            <div>
              <div style={{ fontSize: 14, color: "#fff" }}>Clocked in at {formatTimeWAT(activeClockin.clock_in_at)}</div>
              <div className="text-muted mono" style={{ fontSize: 11, marginTop: 2 }}>
                {activeClockin.lat.toFixed(4)}, {activeClockin.lng.toFixed(4)}
              </div>
            </div>
          </div>
          <button onClick={clockOut} disabled={busy} className="btn-outline w-full tap-target"
                  style={{ padding: "20px", fontSize: 17 }} data-testid="clock-out-btn">
            <LogOutIcon size={18} /> Clock out
          </button>
        </>
      )}

      <div className="s-card">
        <div className="h-section mb-2" style={{ fontSize: 15 }}>Hello, {user.name.split(" ")[0]}</div>
        <div className="text-muted" style={{ fontSize: 13 }}>
          Use the bottom navigation to start a tour, submit a report, or check your tasks.
        </div>
      </div>
    </div>
  );
};
